import { useState, useEffect, useMemo } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { cn } from '@/lib/utils'

interface MarketSession {
    name: string
    flag: string
    timezone: string
    openHour: number
    openMinute: number
    closeHour: number
    closeMinute: number
    alwaysOpen?: boolean
    weekdaysOnly?: boolean
}

const MARKETS: MarketSession[] = [
    { name: 'New York', flag: '🇺🇸', timezone: 'America/New_York', openHour: 9, openMinute: 30, closeHour: 16, closeMinute: 0, weekdaysOnly: true },
    { name: 'London', flag: '🇬🇧', timezone: 'Europe/London', openHour: 8, openMinute: 0, closeHour: 16, closeMinute: 30, weekdaysOnly: true },
    { name: 'Europe', flag: '🇪🇺', timezone: 'Europe/Paris', openHour: 9, openMinute: 0, closeHour: 17, closeMinute: 30, weekdaysOnly: true },
    { name: 'Tokyo', flag: '🇯🇵', timezone: 'Asia/Tokyo', openHour: 9, openMinute: 0, closeHour: 15, closeMinute: 0, weekdaysOnly: true },
    { name: 'Hong Kong', flag: '🇭🇰', timezone: 'Asia/Hong_Kong', openHour: 9, openMinute: 30, closeHour: 16, closeMinute: 0, weekdaysOnly: true },
    { name: 'Singapore', flag: '🇸🇬', timezone: 'Asia/Singapore', openHour: 9, openMinute: 0, closeHour: 17, closeMinute: 0, weekdaysOnly: true },
    { name: 'Sydney', flag: '🇦🇺', timezone: 'Australia/Sydney', openHour: 10, openMinute: 0, closeHour: 16, closeMinute: 0, weekdaysOnly: true },
]

function getTimeParts(date: Date, timezone: string) {
    const fmt = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
        weekday: 'short',
        hour12: false,
    })
    const parts = fmt.formatToParts(date)
    const get = (type: string) => parts.find(p => p.type === type)?.value || '0'
    return {
        hour: parseInt(get('hour'), 10),
        minute: parseInt(get('minute'), 10),
        second: parseInt(get('second'), 10),
        weekday: get('weekday'),
    }
}

function isWeekend(weekday: string) {
    return weekday === 'Sat' || weekday === 'Sun'
}

interface MarketStatus {
    isOpen: boolean
    countdown: string
    localTime: string
    /** 0–1 progress through session (only meaningful when open) */
    progress: number
}

function getMarketStatus(market: MarketSession, now: Date): MarketStatus {
    if (market.alwaysOpen) {
        const { hour, minute } = getTimeParts(now, market.timezone)
        const localTime = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
        return { isOpen: true, countdown: '24 / 7', localTime, progress: 1 }
    }

    const { hour, minute, second, weekday } = getTimeParts(now, market.timezone)
    const currentMinutes = hour * 60 + minute
    const openMinutes = market.openHour * 60 + market.openMinute
    const closeMinutes = market.closeHour * 60 + market.closeMinute
    const sessionLength = closeMinutes - openMinutes

    const localTime = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
    const weekend = market.weekdaysOnly && isWeekend(weekday)

    if (weekend) {
        const daysUntilMon = weekday === 'Sat' ? 2 : 1
        const secondsToMidnight = (23 - hour) * 3600 + (59 - minute) * 60 + (60 - second)
        const totalSeconds = secondsToMidnight + (daysUntilMon - 1) * 86400 + openMinutes * 60
        return { isOpen: false, countdown: formatDuration(totalSeconds), localTime, progress: 0 }
    }

    const isOpen = currentMinutes >= openMinutes && currentMinutes < closeMinutes

    if (isOpen) {
        const elapsed = currentMinutes - openMinutes
        const totalSeconds = (closeMinutes - currentMinutes) * 60 - second
        return { isOpen: true, countdown: formatDuration(totalSeconds), localTime, progress: elapsed / sessionLength }
    }

    if (currentMinutes < openMinutes) {
        const totalSeconds = (openMinutes - currentMinutes) * 60 - second
        return { isOpen: false, countdown: formatDuration(totalSeconds), localTime, progress: 0 }
    }

    const isFriday = weekday === 'Fri'
    const daysToNext = isFriday ? 3 : 1
    const secondsToMidnight = (23 - hour) * 3600 + (59 - minute) * 60 + (60 - second)
    const totalSeconds = secondsToMidnight + (daysToNext - 1) * 86400 + openMinutes * 60
    return { isOpen: false, countdown: formatDuration(totalSeconds), localTime, progress: 0 }
}

function formatDuration(totalSeconds: number): string {
    if (totalSeconds < 0) totalSeconds = 0
    const h = Math.floor(totalSeconds / 3600)
    const m = Math.floor((totalSeconds % 3600) / 60)
    const s = totalSeconds % 60
    if (h > 23) {
        const d = Math.floor(h / 24)
        const rem = h % 24
        return `${d}d ${rem}h ${m}m`
    }
    if (h > 0) return `${h}h ${m}m ${s}s`
    if (m > 0) return `${m}m ${s}s`
    return `${s}s`
}

function MarketCard({ market, now }: { market: MarketSession; now: Date }) {
    const status = useMemo(() => getMarketStatus(market, now), [market, now])

    return (
        <Card className={cn(
            'relative overflow-hidden transition-all duration-300',
            status.isOpen
                ? 'border-emerald-500/30 shadow-[0_0_15px_-3px_rgba(16,185,129,0.15)]'
                : 'border-border/50'
        )}>
            {/* Subtle top accent bar */}
            <div className={cn(
                'absolute top-0 left-0 right-0 h-[2px]',
                status.isOpen
                    ? 'bg-gradient-to-r from-emerald-500/80 via-emerald-400 to-emerald-500/80'
                    : 'bg-gradient-to-r from-transparent via-zinc-600/30 to-transparent'
            )} />

            <CardContent className="p-4 space-y-3">
                {/* Header: flag + name + status dot */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                        <span className="text-xl leading-none">{market.flag}</span>
                        <span className="text-sm font-semibold tracking-tight">{market.name}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <span className={cn(
                            'inline-block w-2 h-2 rounded-full',
                            status.isOpen
                                ? 'bg-emerald-500 shadow-[0_0_6px_rgba(16,185,129,0.6)] animate-pulse'
                                : 'bg-zinc-600'
                        )} />
                        <span className={cn(
                            'text-xs font-semibold uppercase tracking-wider',
                            status.isOpen ? 'text-emerald-500' : 'text-zinc-500'
                        )}>
                            {status.isOpen ? 'Open' : 'Closed'}
                        </span>
                    </div>
                </div>

                {/* Local time */}
                <div className="text-2xl font-mono font-bold tabular-nums tracking-tight text-foreground/90">
                    {status.localTime}
                </div>

                {/* Progress bar (only for open markets with session) */}
                {!market.alwaysOpen && (
                    <div className="w-full h-1 rounded-full bg-muted overflow-hidden">
                        <div
                            className={cn(
                                'h-full rounded-full transition-all duration-1000',
                                status.isOpen
                                    ? 'bg-gradient-to-r from-emerald-500 to-emerald-400'
                                    : 'bg-zinc-700'
                            )}
                            style={{ width: `${Math.max(status.progress * 100, status.isOpen ? 2 : 0)}%` }}
                        />
                    </div>
                )}

                {/* Countdown */}
                <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">
                        {market.alwaysOpen ? 'Always' : status.isOpen ? 'Closes in' : 'Opens in'}
                    </span>
                    <span className={cn(
                        'text-sm font-mono font-semibold tabular-nums',
                        status.isOpen ? 'text-emerald-400' : 'text-muted-foreground'
                    )}>
                        {status.countdown}
                    </span>
                </div>

                {/* Session hours label */}
                {!market.alwaysOpen && (
                    <div className="text-[10px] text-muted-foreground/60 font-mono">
                        {String(market.openHour).padStart(2, '0')}:{String(market.openMinute).padStart(2, '0')}
                        {' – '}
                        {String(market.closeHour).padStart(2, '0')}:{String(market.closeMinute).padStart(2, '0')} local
                    </div>
                )}
            </CardContent>
        </Card>
    )
}

export function MarketHoursWidget() {
    const [now, setNow] = useState(() => new Date())

    useEffect(() => {
        const id = setInterval(() => setNow(new Date()), 1000)
        return () => clearInterval(id)
    }, [])

    const sortedMarkets = useMemo(() => {
        return [...MARKETS].sort((a, b) => {
            const aOpen = getMarketStatus(a, now).isOpen ? 1 : 0
            const bOpen = getMarketStatus(b, now).isOpen ? 1 : 0
            return bOpen - aOpen
        })
    }, [now])

    const openCount = useMemo(
        () => sortedMarkets.filter(m => getMarketStatus(m, now).isOpen).length,
        [sortedMarkets, now]
    )

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between px-1">
                <h2 className="text-sm font-semibold text-muted-foreground tracking-wide uppercase">
                    Market Hours
                </h2>
                <span className="text-xs text-muted-foreground">
                    <span className="text-emerald-500 font-semibold">{openCount}</span> / {MARKETS.length} open
                </span>
            </div>
            <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-7">
                {sortedMarkets.map(market => (
                    <MarketCard key={market.name} market={market} now={now} />
                ))}
            </div>
        </div>
    )
}
